import React, { useState } from 'react';
import { Application, Interview } from '../types';
import { api } from '../services/api';
import { X, Calendar, Video, ShieldAlert } from 'lucide-react';

interface ScheduleInterviewModalProps {
  application: Application | null;
  onClose: () => void;
  onSuccess: (interview: Interview) => void;
}

export const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
  application,
  onClose,
  onSuccess,
}) => {
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!application) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.scheduleInterview({
        application_id: application.id,
        job_title: application.job_title || 'Position',
        candidate_name: application.applicant_name,
        candidate_email: application.applicant_email,
        scheduled_at: scheduledAt,
        notes,
      });

      onSuccess(res.interview);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule interview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div style={{ marginBottom: '20px' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-aqua)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Schedule Video Interview
          </span>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginTop: '4px' }}>
            Candidate: <span className="gradient-text">{application.applicant_name}</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '2px' }}>
            Position: {application.job_title} ({application.job_department})
          </p>
        </div>

        {error && (
          <div
            style={{
              background: 'rgba(244, 63, 94, 0.1)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--accent-rose)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.85rem',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ShieldAlert size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Interview Date & Time</label>
            <input
              type="datetime-local"
              className="form-input"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Interviewer Instructions & Notes</label>
            <textarea
              className="form-textarea"
              placeholder="e.g. System design discussion, plugin coding challenge review, or candidate preparation tips..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              <Video size={16} />
              {loading ? 'Booking...' : 'Schedule & Generate Call Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
